import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';

import type { ColDefIntl } from '../components/BlotterTable/columns';
import { DEFAULT_LOCALE, getIntlKey, IntlContext } from '../contexts/IntlContext';
import { useMixpanel } from '../contexts/MixpanelContext';
import { useHtmlClassObserver } from '../hooks/useHtmlClassObserver';
import { MixpanelEvent } from '../tokens';
import { EMPTY_OBJECT } from '../utils/empty';
import { logger } from '../utils/logger';

const formatCell: ColDefIntl['formatCell'] = (params, messages, displayNameMap) => {
  if (params.context.current.intl.preferServerEnglishText === true) {
    // In this case, we don't want to translate the display name, so we just return the DisplayName value.
    return displayNameMap?.get(params.value)?.DisplayName ?? params.value;
  }

  // Proceed with attempting to translate
  const intlKey = getIntlKey(params.value);
  const translatedValue =
    intlKey in messages ? params.context.current.intl.formatMessage(messages[intlKey]) : undefined;

  // If the translated value is not found, we return the original value
  return translatedValue ?? params.value;
};

export interface IntlProviderProps {
  /**
   * Callback passed in from project-level IntlProvider wrapper components, so that they
   * can reference their own local translations directories, but still have loading and storage of translations
   * managed at a central layer. Should accept a string locale parameter, and return a Promise that resolves to
   * the translations messages, in a Record<string, string> format. Overrides may also be provided,
   * these will not warn on duplicate keys.
   */
  loadMessages?: (locale: string) => {
    messages: Promise<Record<string, string>>;
    overrides?: Promise<Record<string, string>>;
  };
  /**
   * This component does not have access to user preferences, the implementer must provide the locale.
   * If the locale is not provided, the browser locale will be used.
   */
  locale: string | undefined;
  shouldDiscourageLanguageTranslation?: boolean;
}

export function IntlProvider({
  children,
  loadMessages,
  locale: _locale,
  shouldDiscourageLanguageTranslation,
}: PropsWithChildren<IntlProviderProps>) {
  const [locale, setLocale] = useState<string | undefined>(_locale ?? DEFAULT_LOCALE);

  const [isLoaded, setIsLoaded] = useState(false);
  const [messages, setMessages] = useState<Record<string, string> | null>(loadMessages ? null : EMPTY_OBJECT);

  const loadKyokoMessages = useCallback((locale: string) => {
    return import(`../translations/kyoko.${locale}.json`)
      .then(mod => mod.default)
      .catch(error => {
        if (locale.includes('-')) {
          const language = locale.split('-')[0];

          return import(`../translations/kyoko.${language}.json`).then(mod => mod.default);
        }

        throw error;
      });
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (locale == null) {
      return;
    }
    const promises = [
      // Only use the whitelabel-testing subset for testing
      loadKyokoMessages(locale === 'testing' ? 'whitelabel-testing' : locale),
    ];

    if (loadMessages && locale) {
      const { messages, overrides } = loadMessages(locale);
      promises.push(messages);
      if (overrides) {
        promises.push(overrides);
      }
    }

    Promise.all(promises)
      .then(([kyokoMessages, messages = EMPTY_OBJECT, overrides = EMPTY_OBJECT]) => {
        if (!isMounted) {
          return;
        }
        captureExceptionForDuplicates(messages, kyokoMessages);
        setMessages({
          ...kyokoMessages,
          ...messages,
          ...overrides,
        });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        setMessages(EMPTY_OBJECT);
      });

    return () => {
      isMounted = false;
    };
  }, [loadKyokoMessages, loadMessages, locale]);

  useEffect(() => {
    if (locale && messages) {
      setIsLoaded(true);
    }
  }, [locale, messages]);

  const value = useMemo(() => {
    return {
      isLoaded,
      locale: locale ?? DEFAULT_LOCALE,
      setLocale,
      // [DEAL-4794] Do not overwrite with English translations if the user's locale is English
      preferServerEnglishText: locale?.split('-').at(0) === 'en',
      formatCell,
    };
  }, [isLoaded, locale]);

  const onIntlError = useCallback(
    (error: Error) => {
      if (locale === 'testing') {
        console.error('Intl Error:', error.message);
      }
    },
    [locale]
  );

  const onIntlWarn = useCallback(
    (warning: string) => {
      if (locale === 'testing') {
        console.warn('Intl Warning:', warning);
      }
    },
    [locale]
  );
  const mixpanel = useMixpanel();

  const onHandleClassNameChange = useCallback(
    (classNames: string) => {
      if (classNames.includes('translated')) {
        // its very likely that user is using google translate to translate the page
        // so lets track it to try to figure out how widespread it is
        mixpanel.track(MixpanelEvent.PageTranslated);
      }
    },
    [mixpanel]
  );
  // whenever the class name changes, we check if it is translated-ltr (or includes "translated")
  useHtmlClassObserver({ onChange: onHandleClassNameChange });

  // If we have set the locale, and the org config prevents translation, set root html translate attribute to "no"
  useEffect(() => {
    document.documentElement.setAttribute('lang', locale ?? 'en');
    if (shouldDiscourageLanguageTranslation === true) {
      document.documentElement.setAttribute('translate', 'no');
    } else {
      document.documentElement.removeAttribute('translate');
    }
  }, [shouldDiscourageLanguageTranslation, locale]);

  return (
    <ReactIntlProvider
      // A valid locale must to be passed in, so we default to DEFAULT_LOCALE if the locale is 'testing'
      locale={value.locale === 'testing' ? DEFAULT_LOCALE : value.locale}
      messages={messages ?? EMPTY_OBJECT}
      onError={onIntlError}
      onWarn={onIntlWarn}
    >
      <IntlContext.Provider value={value}>{children}</IntlContext.Provider>
    </ReactIntlProvider>
  );
}

function captureExceptionForDuplicates(messages: Record<string, string>, kyokoMessages: Record<string, string>) {
  Object.keys(messages).forEach(key => {
    if (kyokoMessages[key]) {
      logger.error(new Error(`Duplicate defineMessages id found: ${key}`));
    }
  });
}
